/**
 * The "Connect Moodle" section of `/settings/account` (#497, moved from `/setup` by #581): where a learner pastes their own Moodle key.
 *
 * The key is a credential. It lives in this component's state until submit and is cleared the moment the request is
 * sent, whatever the outcome. It travels only in the POST body: never in a URL, a query key, storage or telemetry, and
 * never in any response (the server does not echo it). Its field is a password input with autocomplete off.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiRequestError } from '../../api/client.js';
import { queryKeys } from '../../api/query-keys.js';
import { useApi } from '../../app/api-context.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { useMessages } from '../../i18n/locale.js';
import { defineMessages } from '../../i18n/messages.js';

export const moodleMessages = defineMessages({
  en: {
    heading: 'Moodle',
    connected: 'Moodle is connected.',
    notConnected: 'Moodle is not connected yet.',
    reads:
      'Read-only: your name, your courses, course contents and assignments. DaVinci never submits, never sees grades and never sees your password.',
    step1: 'In Moodle, open the profile menu, then Preferences, then User account, then Security keys.',
    openKeys: 'Open my Moodle security keys',
    step2: 'Copy the key called "Moodle mobile web service".',
    step3: 'Paste it here and press Connect.',
    tokenLabel: 'Moodle key',
    connect: 'Connect',
    connecting: 'Connecting…',
    disconnect: 'Disconnect',
    justConnected: 'Connected. Your courses are on their way.',
    justConnectedPartial: 'Connected, but some of your course files could not be read yet. They will be tried again.',
    malformed: 'That does not look like a Moodle key. Copy the whole key and try again.',
    rejected: 'Moodle did not accept that key. Make sure it is the "Moodle mobile web service" key and try again.',
    unreachable: 'Moodle did not answer. Try again in a little while.',
  },
  he: {
    heading: 'מודל',
    connected: 'המודל מחובר.',
    notConnected: 'המודל עוד לא מחובר.',
    reads:
      'קריאה בלבד: השם, הקורסים, תוכן הקורסים והמטלות. DaVinci לא מגישה, לא רואה ציונים ולא רואה את הסיסמה.',
    step1: 'במודל פותחים את תפריט הפרופיל, אחר כך העדפות, חשבון משתמש ומפתחות אבטחה.',
    openKeys: 'פתיחת מפתחות האבטחה שלי במודל',
    step2: 'מעתיקים את המפתח בשם "Moodle mobile web service".',
    step3: 'מדביקים אותו כאן ולוחצים על חיבור.',
    tokenLabel: 'מפתח מודל',
    connect: 'חיבור',
    connecting: 'מתחבר…',
    disconnect: 'ניתוק',
    justConnected: 'החיבור הצליח. הקורסים בדרך.',
    justConnectedPartial: 'החיבור הצליח, אבל חלק מקבצי הקורסים עוד לא נקראו. ננסה שוב.',
    malformed: 'זה לא נראה כמו מפתח של מודל. כדאי להעתיק את המפתח כולו ולנסות שוב.',
    rejected: 'מודל לא קיבל את המפתח הזה. כדאי לוודא שזה המפתח "Moodle mobile web service" ולנסות שוב.',
    unreachable: 'מודל לא ענה. כדאי לנסות שוב בעוד זמן קצר.',
  },
});

/**
 * The connection query and the connect call, shared by Settings and the welcome flow (#648): one request, one place the
 * key is handled. The caller clears the key the moment it is sent.
 */
export function useMoodleConnect() {
  const t = useMessages(moodleMessages);
  const api = useApi();
  const queryClient = useQueryClient();
  const connection = useQuery({ queryKey: queryKeys.moodleConnection, queryFn: () => api.getMoodleConnection() });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.moodleConnection });
    void queryClient.invalidateQueries({ queryKey: queryKeys.courses });
  };
  const connect = useMutation({
    mutationFn: (value: string) => api.connectMoodle({ token: value }),
    onSettled: refresh,
  });
  const disconnect = useMutation({ mutationFn: () => api.disconnectMoodle(), onSettled: refresh });

  const code = connect.error instanceof ApiRequestError ? connect.error.code : undefined;
  const sentence =
    code === 'moodle_token_malformed'
      ? t.malformed
      : code === 'moodle_token_rejected'
        ? t.rejected
        : code === 'moodle_unreachable'
          ? t.unreachable
          : undefined;
  const connected = connection.data?.connected === true;
  // The page address comes from the server (#660); the web never builds it. Null or absent: describe the path in words.
  const securityKeysUrl = connection.data?.securityKeysUrl ?? null;
  return { connection, connect, disconnect, sentence, connected, securityKeysUrl };
}

/** The external-link glyph the boards draw beside a link that leaves DaVinci. */
export function ExternalLinkIcon(): React.JSX.Element {
  return (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 4h6v6" />
      <path d="M20 4l-9 9" />
      <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </svg>
  );
}

export function MoodleSection(): React.JSX.Element {
  const t = useMessages(moodleMessages);
  const [token, setToken] = useState('');
  const { connection, connect, disconnect, sentence, connected, securityKeysUrl } = useMoodleConnect();

  function submit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const value = token.trim();
    if (value === '') return;
    setToken('');
    connect.mutate(value);
  }

  return (
    <section className="settings-card moodle-section" id="moodle-connection" aria-labelledby="moodle-heading">
      <h3 id="moodle-heading">{t.heading}</h3>
      {connection.isError ? <FailureNotice error={connection.error} onRetry={() => void connection.refetch()} /> : null}
      {connection.isSuccess ? (
        <p className="settings-status">
          <span className="settings-dot" data-on={connected} aria-hidden="true" />
          <strong>{connected ? t.connected : t.notConnected}</strong>
        </p>
      ) : null}

      {connect.isSuccess ? (
        <p role="status">{connect.data.anyReadFailed ? t.justConnectedPartial : t.justConnected}</p>
      ) : null}
      {sentence !== undefined ? (
        <p className="notice notice-error" role="alert">
          {sentence}
        </p>
      ) : connect.isError ? (
        <FailureNotice error={connect.error} />
      ) : null}

      {connected ? (
        <>
          <p className="field-hint">{t.reads}</p>
          <button
            type="button"
            className="button-neutral settings-button"
            onClick={() => disconnect.mutate()}
            disabled={disconnect.isPending}
          >
            {t.disconnect}
          </button>
        </>
      ) : (
        <>
          <ol className="settings-steps">
            <li>
              {securityKeysUrl !== null ? (
                <a className="moodle-keys-link" href={securityKeysUrl} target="_blank" rel="noopener noreferrer">
                  <span>{t.openKeys}</span>
                  <ExternalLinkIcon />
                </a>
              ) : (
                t.step1
              )}
            </li>
            <li>{t.step2}</li>
            <li>{t.step3}</li>
          </ol>
          <form className="settings-inline-form" onSubmit={submit}>
            <label htmlFor="moodle-token" className="settings-form-label">
              {t.tokenLabel}
            </label>
            <div className="settings-form-row">
              <input
                id="moodle-token"
                name="moodleKey"
                type="password"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                value={token}
                onChange={(event) => setToken(event.target.value)}
              />
              <button type="submit" disabled={connect.isPending}>
                {connect.isPending ? t.connecting : t.connect}
              </button>
            </div>
          </form>
        </>
      )}
      {disconnect.isError ? <FailureNotice error={disconnect.error} /> : null}
    </section>
  );
}
